import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, effect, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuModule } from 'primeng/menu';
import { combineLatest } from 'rxjs';
import { SubSink } from 'subsink';


import { ColorSchemeService } from '../../color-scheme.service';
import { DISPATCH_PERMISSIONS, FRONTEND_PERMISSIONS } from '../../permissions.constants';
import { BrandingService } from '../../services/branding.service';
import { PermissionService } from '../../services/permission.service';
import { KarveTabviewComponent } from '../../shared/karve-tabview/karve-tabview.component';
import { ScheduleControlsBarComponent } from '../schedule-controls/schedule-controls-bar.component';
import { ScheduleControlsService } from '../schedule-controls/schedule-controls.service';

/** Breadcrumb items of the schedule page. */
type ScheduleCrumb = MenuItem;

@Component({
    selector: 'app-schedule-page',
    templateUrl: './schedule-page.component.html',
    styleUrl: './schedule-page.component.scss',
    standalone: true,
    imports: [
        BreadcrumbModule,
        CommonModule,
        KarveTabviewComponent,
        ScheduleControlsBarComponent,
        MenuModule,
        RouterOutlet,
    ],
})
export class SchedulePageComponent implements OnInit, OnDestroy {

    home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

    /** The last crumb (the current tab's name) is clickable and opens the
     *  tab picker; the section crumb is the static page name. MenuItem
     *  type for PrimeNG; a command-less item renders as static text. */
    breadcrumb: ScheduleCrumb[] = [];

    pageMenu: MenuItem[] = [];

    /** The picker's popup menu, toggled from the tab crumb (Ray). */
    @ViewChild('pageMenuTrigger') pageMenuModel: any;

    /** The week tab's one-click date picker (rendered only on that tab);
     *  hidden programmatically after the span pick. */
    // (moved into app-crumb-date-picker with the rest of the pill's pickers)

    public tabs: { name: string; slug: string; permission?: string }[] = [];

    /** The active child route's slug (day/week/month/dispatch/map/ops). */
    private activeTabSlug = '';

    /** Permission gates for optional tabs, consulted on every recompute. */
    private tabPermissions: Record<string, string> = {
        'ops-readiness': FRONTEND_PERMISSIONS.dashboardOpsReadiness,
    };

    private subs = new SubSink();
    private controls = inject(ScheduleControlsService);
    private router = inject(Router);

    constructor(
        private brandingService: BrandingService,
        public colorScheme: ColorSchemeService,
        private permissionService: PermissionService,
        private route: ActivatedRoute,
    ) {
        // The tab crumb mirrors the active tab: rebuild whenever the shared
        // slug changes so the label shows the CURRENT tab (deeper: a child
        // tab's own config write can race this parent's just-checked
        // template (NG0100 in dev), hence the microtask deferral).
        effect(() => {
            this.controls.activeTabSlug();
            queueMicrotask(() => this.rebuildBreadcrumb());
        });
    }

    /** Reads the active child tab's slug out of the router URL and shares
     *  it on the service (the bar's pill picks its variant from the same
     *  signal). */
    private syncActiveTabSlug(url: string): void {
        const path = url.split('?')[0].replace(/\/+$/, '');
        const parts = path.split('/');
        const idx = parts.indexOf('schedule');
        this.activeTabSlug = idx >= 0 && parts[idx + 1] ? parts[idx + 1] : this.activeTabSlug;
        this.controls.activeTabSlug.set(this.activeTabSlug);
    }

    /** day/week/month render the full-bleed schedule card: the panel hosts
     *  it with NO padding so the calendar's borders butt the card's edges
     *  (Ray; the other tabs keep their breathing room). */
    get flushScheduleTab(): boolean {
        return ['day', 'week', 'month'].includes(this.activeTabSlug);
    }

    ngOnInit(): void {
        this.watchDispatchFeature();
        // Keep the breadcrumb in sync with the URL's tab: the tab crumb's
        // label is the active tab's name (Ray). Drive from NavigationEnd so
        // the slug is correct even when the router outlet hasn't created the
        // child yet (route.firstChild can be null at init) and survives
        // direct deep links / navigations between the tabs.
        this.syncActiveTabSlug(this.router.routerState.snapshot.url);
        this.subs.sink = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.syncActiveTabSlug(event.urlAfterRedirects ?? event.url);
            }
        });
    }

    private rebuildBreadcrumb(): void {
        this.breadcrumb = [
            // The section crumb is now the STATIC page name (Ray: "change
            // the day/week/month in the breadcrumbs to be the thing that is
            // clickable … not 'Schedule'"), so the LAST crumb — the current
            // tab's name — is the switcher: clicking it opens the tab menu
            // (day/week/month/dispatch/map/ops readiness, each carrying the
            // zone/date window across). The popup positions itself from the
            // click event, so it lands next to the tab crumb.
            { label: 'Schedule' },
            // Last item ⇒ PrimeNG renders no separator after it.
            // The crumb's tabindex is passed to PrimeNG's WRAPPING anchor
            // ([attr.tabindex]=item.tabindex) — making that anchor untabbable
            // leaves the role-bearing label span as the SINGLE tab stop
            // (CodeRabbit round 30: PrimeNG's anchor + our span used to be
            // two separate stops, and Enter never reached the span).
            // Mouse click still works: the anchor's click still fires the
            // command with the originalEvent.
            {
                label: this.activeTabLabel(),
                command: (e: { originalEvent: Event }) => this.showPageMenu(e.originalEvent),
                tabindex: '-1',
            },
        ];
    }

    /** The current tab's display name for the crumb (falls back to the
     *  capitalized slug, e.g. a deep-linked tab not in this.tabs yet). */
    private activeTabLabel(): string {
        const name = this.tabs.find((tab) => tab.slug === this.activeTabSlug)?.name;
        if (name) { return name; }
        const slug = this.activeTabSlug;
        return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : '';
    }

    /** Breadcrumb page picker (the dashboards' click-to-change affordance). */
    buildPageMenu(): void {
        this.pageMenu = this.tabs.map((tab) => ({
            label: tab.name,
            command: () => {
                // carry the current zone/date window across tabs
                const search = new URL(window.location.href).searchParams;
                const queryParams: Record<string, string> = {};
                for (const key of ['zone', 'date', 'dateTo']) {
                    const value = search.get(key);
                    if (value) { queryParams[key] = value; }
                }
                this.router.navigate(['/schedule', tab.slug], { queryParams, queryParamsHandling: 'merge' });
            },
        }));
    }

    showPageMenu(event: Event): void {
        this.pageMenuModel?.toggle(event);
    }

    private watchDispatchFeature() {
        this.subs.sink = combineLatest([
            this.brandingService.watchFeature('dispatch'),
            this.permissionService.watchPermission(DISPATCH_PERMISSIONS.viewDispatch),
            this.permissionService.watchPermission(DISPATCH_PERMISSIONS.viewMap),
            // The Ops Readiness tab's permission must also re-trigger the
            // rebuild — a change while dispatch/map stay put would otherwise
            // leave the tab list stale (CodeRabbit).
            this.permissionService.watchPermission(this.tabPermissions['ops-readiness']),
        ]).subscribe(([featureEnabled, canViewDispatch, canViewMap]) => {
            this.tabs = [
                { name: 'Day', slug: 'day' },
                { name: 'Week', slug: 'week' },
                { name: 'Month', slug: 'month' },
            ];

            if (featureEnabled) {
                if (canViewDispatch) {
                    this.tabs = [ ...this.tabs, { name: 'Dispatch', slug: 'dispatch' } ];
                }
                if (canViewMap) {
                    this.tabs = [ ...this.tabs, { name: 'Map', slug: 'map' } ];
                }
            }

            if (this.tabPermissions['ops-readiness'] &&
                this.permissionService.checkPermission(this.tabPermissions['ops-readiness'])) {
                this.tabs = [...this.tabs, {
                    name: 'Ops Readiness',
                    slug: 'ops-readiness',
                    permission: this.tabPermissions['ops-readiness'],
                }];
            }
            this.buildPageMenu();
            // The tab CRUMB must also pick up a late tab-name (CodeRabbit):
            // the slug effect only reads the slug signal — if Ops Readiness'
            // permission arrives after it last ran, ops-readiness would stay
            // a capitalized-slug crumb. Rebuild when the LIST changes, with
            // the same microtask deferral the slug effect uses (NG0100).
            queueMicrotask(() => this.rebuildBreadcrumb());
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}